import * as React from 'react';
import { useEffect, useRef } from 'react';
import { GrapeRipeRotApiResponse } from '../../components/models/grapeRipeRot/grapeRipeRotTypes';

const thClass =
  'sticky top-0 px-6 py-3 text-xs leading-4 tracking-wider text-white border-b border-r border-gray-200 bg-secondary-800';
const thFirstClass =
  'sticky top-0 px-6 py-3 text-xs leading-4 tracking-wider text-center text-white border-r border-gray-200 bg-secondary-800';

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDatetime(datetimeStr: string): string {
  const d = new Date(datetimeStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const downloadButtonClass =
  'inline-flex items-center p-2 text-sm font-medium leading-4 text-white transition duration-150 ease-in-out border border-transparent rounded-md sm:px-3 sm:py-2 bg-secondary-800 hover:bg-secondary-700 focus:outline-none focus:border-secondary-900 focus:shadow-outline-secondary active:bg-secondary-900';

interface ResultsTableProps {
  riskData: GrapeRipeRotApiResponse | null;
  hourlyExpanded: boolean;
  onToggleHourly: () => void;
  onDownloadCSV: () => void;
}

export function ResultsTable({ riskData, hourlyExpanded, onToggleHourly, onDownloadCSV }: ResultsTableProps) {
  if (!riskData) return null;

  return (
    <>
      <DailyResultsTable dailyData={riskData.dailyData}/>
      <HourlyResultsTable
        hourlyData={riskData.hourlyData}
        expanded={hourlyExpanded}
        onToggle={onToggleHourly}
        onDownloadCSV={onDownloadCSV}
      />
    </>
  );
}

function DailyResultsTable({ dailyData }: { dailyData: GrapeRipeRotApiResponse['dailyData'] }) {
  if (!dailyData || dailyData.length === 0) return null;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (todayRef.current && wrapperRef.current) {
      const container = wrapperRef.current;
      const row = todayRef.current;
      container.scrollTop =
        row.offsetTop - container.clientHeight / 2 + row.clientHeight / 2;
    }
  }, [dailyData]);

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  return (
    <div className="w-full max-w-5xl mx-auto mt-8 px-5 mb-16 sm:mb-18 md:mb-20">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-semibold text-gray-600 md:text-2xl">
          Daily Results
        </h2>
      </div>

      <div className="flex flex-col my-4 sm:flex-row sm:justify-between sm:items-center">
        <div className="flex flex-col mt-2 text-sm lg:mt-0 lg:items-center lg:flex-row"></div>
        <div className="flex flex-col mt-2 text-sm lg:mt-0 lg:items-center lg:flex-row">
          <span className="mb-2 mr-3 font-bold tracking-wider text-gray-700 lg:mb-0">
            Risk:
          </span>
          <div className="grid grid-cols-2 gap-1">
            <span
              className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md text-xs lg:text-sm font-bold text-white bg-red-800">
              High
            </span>
            <span
              className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md text-xs lg:text-sm font-bold text-white bg-green-500">
              Low
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col mt-4">
        <div className="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <div
            ref={wrapperRef}
            className="inline-block min-w-full overflow-hidden align-middle border-b border-gray-200 shadow sm:rounded-lg max-h-128 overflow-y-auto"
          >
            <table className="min-w-full max-h-128">
              <thead>
              <tr>
                <th className={thFirstClass}>Date</th>
                <th className={thClass}>Mean Temp (&deg;F)</th>
                <th className={thClass}>Leaf Wetness (hours)</th>
                <th className={thClass}>Max Env. Risk</th>
                <th className={thClass}>Max Ripe Rot Risk</th>
                <th className={thClass}>High Risk</th>
              </tr>
              </thead>
              <tbody className="bg-white">
              {dailyData.map((row, index) => {
                const riskClass = row.isHighRisk
                  ? 'text-white bg-red-800'
                  : 'text-white bg-green-500';
                const isToday = row.date === todayStr;

                return (
                  <tr
                    key={index}
                    ref={isToday ? todayRef : undefined}
                    className={isToday ? 'bg-yellow-50' : ''}
                  >
                    <td className="td py-2">
                        <span className="cell flex flex-col items-center">
                          {row.date ? formatDate(row.date) : ''}
                          {row.date > todayStr && (<span className="text-xs text-center">Forecast</span>)}
                        </span>
                    </td>
                    <td className="td py-2">
                        <span className="cell">
                          {row.meanTemperatureC != null
                            ? (row.meanTemperatureC * 9 / 5 + 32).toFixed(1)
                            : '—'}
                        </span>
                    </td>
                    <td className="td py-2">
                      <span className="cell">{row.lwdHours ? Math.round(row.lwdHours) : '—'}</span>
                    </td>
                    <td className="td py-2">
                        <span className="cell">
                          {row.dailyMaxEnvironmentalRisk != null
                            ? row.dailyMaxEnvironmentalRisk.toFixed(2)
                            : '—'}
                        </span>
                    </td>
                    <td className="td py-2">
                        <span className="cell">
                          {row.dailyMaxRipeRotRisk != null
                            ? row.dailyMaxRipeRotRisk.toFixed(2)
                            : '—'}
                        </span>
                    </td>
                    <td className="td py-2">
                        <span className={`cell ${riskClass}`}>
                          {row.isHighRisk ? 'Yes' : 'No'}
                        </span>
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <p className="mt-4 text-sm">
        Infection events, shown in red above are based on the ripe rot model risk
        being greater than 0.45. The ripe rot model risk is devised from a combination
        of the "Max Env. Risk" or maximum weather-related risk and the phenological
        stage susceptibility. The Max Env. Risk is calculated with the df2-nn model
        from Cosseboom and Hu (2023) and uses leaf wetness duration and temperature as
        the parameters.
      </p>
    </div>
  );
}

function HourlyResultsTable({
  hourlyData,
  expanded,
  onToggle,
  onDownloadCSV,
}: {
  hourlyData: GrapeRipeRotApiResponse['hourlyData'];
  expanded: boolean;
  onToggle: () => void;
  onDownloadCSV: () => void;
}) {
  if (!hourlyData || hourlyData.length === 0) return null;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const currentHourRef = useRef<HTMLTableRowElement>(null);

  const lastHistoricalIndex = hourlyData
    .map((h, i) => (!h.isForecast ? i : -1))
    .filter((i) => i !== -1)
    .at(-1);

  useEffect(() => {
    if (expanded && currentHourRef.current && wrapperRef.current) {
      const container = wrapperRef.current;
      const row = currentHourRef.current;
      container.scrollTop =
        row.offsetTop - container.clientHeight / 2 + row.clientHeight / 2;
    }
  }, [expanded, hourlyData]);

  return (
    <div className="w-full max-w-5xl mx-auto mt-8 px-5 mb-16 sm:mb-18 md:mb-20">
      <div
        className="flex items-center justify-between mb-3 cursor-pointer"
        onClick={onToggle}
      >
        <h2 className="text-xl font-semibold text-gray-600 md:text-2xl">
          Hourly Results
        </h2>
        <span className="text-gray-600 text-xl">{expanded ? '▼' : '►'}</span>
      </div>

      {expanded && (
        <>
          <div className="flex flex-col my-4 sm:flex-row sm:justify-between sm:items-center">
            <div className="flex flex-col mt-2 text-sm lg:mt-0 lg:items-center lg:flex-row">
              <span className="mb-2 mr-3 font-bold tracking-wider text-gray-700 lg:mb-0">
                Events:
              </span>
              <div className="grid grid-cols-2 gap-1">
                <span
                  className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md text-xs lg:text-sm font-bold text-white bg-blue-500">
                  Wet
                </span>
                <span
                  className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md text-xs lg:text-sm font-bold text-yellow-900 bg-yellow-300">
                  Dry
                </span>
              </div>
            </div>

            <div className="flex justify-center rounded-md shadow-sm mt-2 lg:mt-0">
              <button
                type="button"
                aria-label="download hourly results data in csv format"
                className={downloadButtonClass}
                onClick={onDownloadCSV}
              >
                <svg
                  className="-ml-0.5 mr-2 h-4 w-4"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
                <span className="hidden sm:inline-block">Download</span>{' '}
                <span>CSV</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col mt-4">
            <div className="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
              <div
                ref={wrapperRef}
                className="inline-block min-w-full overflow-hidden align-middle border-b border-gray-200 shadow sm:rounded-lg max-h-128 overflow-y-auto"
              >
                <table className="min-w-full max-h-128">
                  <thead>
                  <tr>
                    <th className={thFirstClass}>Date/Time</th>
                    <th className={thClass}>Temp (&deg;F)</th>
                    <th className={thClass}>Leaf Wetness (min)</th>
                    <th className={thClass}>RH (%)</th>
                    <th className={thClass}>Wetness Event</th>
                    <th className={thClass}>Env. Risk</th>
                    <th className={thClass}>Ripe Rot Risk</th>
                  </tr>
                  </thead>
                  <tbody className="bg-white">
                  {hourlyData.map((row, index) => {
                    const isCurrentHour = index === lastHistoricalIndex;
                    const wetnessClass =
                      row.wetnessEventStatus === 1
                        ? 'text-white bg-blue-500'
                        : 'text-yellow-900 bg-yellow-300';
                    const leafWetnessClass =
                      row.leafWetnessMinutes != null &&
                      row.leafWetnessMinutes > 0
                        ? 'text-white bg-blue-500'
                        : 'text-yellow-900 bg-yellow-300';

                    return (
                      <tr
                        key={index}
                        ref={isCurrentHour ? currentHourRef : undefined}
                        className={isCurrentHour ? 'bg-yellow-50' : ''}
                      >
                        <td className="td py-2">
                            <span className="cell flex flex-col items-center">
                              {row.datetime ? formatDatetime(row.datetime) : ''}
                              {row.isForecast && (
                                <span className="text-xs text-center">
                                  Forecast
                                </span>
                              )}
                            </span>
                        </td>
                        <td className="td py-2">
                            <span className="cell">
                              {row.temperatureC != null
                                ? (row.temperatureC * 9 / 5 + 32).toFixed(1)
                                : '—'}
                            </span>
                        </td>
                        <td className="td py-2">
                            <span className={`cell ${leafWetnessClass}`}>
                              {row.leafWetnessMinutes ?? '—'}
                            </span>
                        </td>
                        <td className="td py-2">
                            <span className="cell">
                              {row.relativeHumidity != null
                                ? row.relativeHumidity.toFixed(0)
                                : '—'}
                            </span>
                        </td>
                        <td className="td py-2">
                            <span className={`cell ${wetnessClass}`}>
                              {row.wetnessEventStatus != null
                                ? row.wetnessEventStatus === 1
                                  ? 'Yes'
                                  : 'No'
                                : '—'}
                            </span>
                        </td>
                        <td className="td py-2">
                            <span className="cell">
                              {row.hourlyGrapeRipeRotEnvironmentalRisk != null
                                ? row.hourlyGrapeRipeRotEnvironmentalRisk.toFixed(
                                  2
                                )
                                : '—'}
                            </span>
                        </td>
                        <td className="td py-2">
                            <span className="cell">
                              {row.hourlyGrapeRipeRotRisk != null
                                ? row.hourlyGrapeRipeRotRisk.toFixed(2)
                                : '—'}
                            </span>
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
